import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useCallback, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import { requests } from "../../../../services/http-common";
import { If } from "../../../../components/If/If";
import { SectionHeader } from "../../../../components/SectionHeader/SectionHeader";
import { LabelValue } from "../../../../components/LabelValue/LabelValue";
import { FormFieldsPanel } from "../../../../components/form-fields-panel/form-fields-panel";
import { useToast } from "../../../../Providers/ToastProvider";

import classes from "./ContractDetails.module.scss";

const display = (input: unknown) =>
  input === null || input === undefined || input === ""
    ? "—"
    : String(input);

const stripHtml = (html: unknown) => {
  if (!html) return "";
  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

export const ContractDetails = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const [filePath, setFilePath] = useState("");
  const { show } = useToast();

  const { data } = useQuery({
    queryKey: [`contract-form-${contractId}`],
    queryFn: () => {
      return requests.get(`/contract/${contractId}`);
    },
    onSuccess: (data) => {
      if (data.pdfPath) {
        setFilePath(
          `${process.env.REACT_APP_DSC_SERVICE_DOMAIN}/${data.pdfPath}`
        );
      }
    },
  });

  const handleViewDocument = useCallback(
    async (documentId: string) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_DSC_SERVICE_URL}/contract/${contractId}/signed-document/${documentId}`,
          { responseType: "blob" }
        );
        const contentType =
          response.headers["content-type"] || "application/pdf";
        const url = URL.createObjectURL(
          new Blob([response.data], { type: contentType })
        );
        window.open(url, "_blank");
        setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      } catch (error: any) {
        show({
          severity: "error",
          detail: "Failed to open signed document",
        });
      }
    },
    [contractId, show]
  );

  return data ? (
    <div className={classes.container}>
      <SectionHeader title={`Contract N ${data.contractN}`} />

      <FormFieldsPanel label="Contract" className={classes.panel}>
        <div className={classes.fields}>
          <LabelValue label="Contract number" value={display(data.contractN)} />
          <LabelValue label="Status" value={display(data.status)} />
          <LabelValue
            label="Contract type"
            value={display(data.contractName)}
          />
          <LabelValue label="Amount" value={display(data.amount)} />
          <LabelValue
            label="Cancellation notice"
            value={display(data.cancellation)}
          />
          <LabelValue
            label="Estimated SQFT"
            value={display(data.estimatedSQFT)}
          />
          <LabelValue
            label="Type of space"
            value={display(data.typeOfSpace)}
          />
          <LabelValue label="Pets" value={display(data.pets)} />
          <LabelValue label="Recurrence" value={display(data.recurrence)} />
          <LabelValue label="Frequency" value={display(data.frequency)} />
        </div>
      </FormFieldsPanel>

      <FormFieldsPanel label="Compensation" className={classes.panel}>
        <div className={classes.fields}>
          <LabelValue
            label="Compensation"
            value={display(stripHtml(data.compensation))}
          />
        </div>
      </FormFieldsPanel>

      <If condition={!!data.sowFrequencies?.length}>
        <FormFieldsPanel label="Scope of work" className={classes.panel}>
          <DataTable value={data.sowFrequencies}>
            <Column field="name" header="Area / Task" />
            <Column field="frequency" header="Frequency" />
          </DataTable>
        </FormFieldsPanel>
      </If>

      <FormFieldsPanel label="Client" className={classes.panel}>
        <div className={classes.fields}>
          <LabelValue
            label="Name / Company"
            value={display(data.lead?.companyName || data.lead?.name)}
          />
          <LabelValue
            label="Contact name"
            value={display(data.lead?.contactName)}
          />
          <LabelValue label="Email" value={display(data.lead?.email)} />
          <LabelValue label="Phone" value={display(data.lead?.phone)} />
          <LabelValue label="Address" value={display(data.lead?.address)} />
        </div>
      </FormFieldsPanel>

      <If condition={!!data.signedDocuments?.length}>
        <FormFieldsPanel label="Documents" className={classes.panel}>
          <DataTable value={data.signedDocuments}>
            <Column field="fileName" header="File name" />
            <Column
              field="kind"
              header="Kind"
              body={(rowData) => display(rowData.kind || "signed")}
            />
            <Column
              header=""
              body={(rowData) => (
                <button
                  type="button"
                  className={classes.linkButton}
                  onClick={() => handleViewDocument(rowData.id)}
                >
                  View
                </button>
              )}
            />
          </DataTable>
        </FormFieldsPanel>
      </If>

      <If condition={!!filePath}>
        <embed
          className={classes.report}
          src={`${filePath}?timestamp=${new Date().getTime()}`}
          type="application/pdf"
        />
      </If>
    </div>
  ) : null;
};
